// Web Search Service for Lead Research
import { apiClient } from '../ai/config/api.js';

class WebSearchService {
    constructor() {
        this.apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    }

    /**
     * Comprehensive Podcaster-focused research
     * Finds background, hobbies, news, occupations, and formats with sources.
     */
    async getPodcasterResearch(guestName, companyName = '', user = null) {
        try {
            const token = user ? await user.getIdToken() : '';
            const fullQuery = companyName ? `${guestName} ${companyName}` : guestName;

            // 1. Get deep data from Tavily
            const response = await fetch(`${this.apiBaseUrl}/api/web-search/tavily`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    query: `${fullQuery} biography professional background hobbies personal life news interviews`,
                    searchType: 'deep',
                    maxResults: 10
                })
            });

            if (!response.ok) throw new Error('Search failed');
            const data = await response.json();

            // 2. Use AI to format this into a "Podcaster Dossier"
            const aiPrompt = `
You are an expert podcast researcher. Create a comprehensive "Guest intelligence" report for ${guestName} using ONLY the provided search results.

INSTRUCTIONS:
- Format as clean Markdown.
- Include the following sections:
  1. **Background & Career**: Their professional journey and current role.
  2. **Life & Hobbies**: Any personal details, interests, or unique background stories found. (If you find anything about where they grew up, schools, or family, put it here).
  3. **Recent News & Impact**: What they've been doing lately, major announcements, or news articles.
  4. **Engagement Nuggets**: 2-3 unique or surprising facts/conversational hooks that a host could use to start the interview.
  5. **Verified Sources**: List the URLs found as clickable links.

SEARCH DATA:
${data.answer || ''}
${data.results?.map(r => `Title: ${r.title}\nContent: ${r.content}\nURL: ${r.url}`).join('\n---\n')}

STRICT RULES:
- If info for a section is missing, keep it brief or mention "Not found in public records".
- DO NOT hallucinate.
- Use the sources provided.
`;

            const finalDossier = await apiClient.generateContent(aiPrompt, 'gpt-4o', {
                temperature: 0.1,
                max_tokens: 1500
            });

            return finalDossier;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get recent news about a guest or company (Structured)
     */
    async getRecentNews(query, companyName = '', user = null) {
        try {
            const token = user ? await user.getIdToken() : '';
            const fullQuery = companyName ? `${query} ${companyName}` : query;

            const response = await fetch(`${this.apiBaseUrl}/api/web-search/tavily`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    query: `${fullQuery} recent news accomplishments 2024 2025`,
                    searchType: 'deep'
                })
            });

            if (!response.ok) throw new Error('News search failed');
            return await response.json();
        } catch (error) {
            throw error;
        }
    }
}

export default new WebSearchService();
